import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getStoreUrl } from '../../config/platformConfig';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import QRCode from 'qrcode';
import Shell from '../dashboard/Shell';

const ViewStore = () => {
  const { current: store } = useSelector(s => s.store || {});
  const { getAxios } = useSecureAuth();
  const toast = useToast();
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    totalOrders: 0,
    storeViews: 0
  });

  useEffect(() => {
    if (store) {
      const storeUrl = getStoreUrl(store.slug);

      QRCode.toDataURL(storeUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      }).then(url => {
        setQrCodeDataUrl(url);
      }).catch(err => {
        console.error('Error generating QR code:', err);
      });

      fetchStats();
    }
  }, [store]);

  const fetchStats = async () => {
    try {
      const axios = getAxios();
      // TODO: Implement actual API calls
      setStats({
        totalProducts: 12,
        activeProducts: 10,
        totalOrders: 45,
        storeViews: 234
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  if (!store) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <p style={{ color: '#6b7280', fontSize: '0.9375rem' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const storeUrl = getStoreUrl(store.slug);

  const shareOnWhatsApp = () => {
    const text = `Check out my store: ${storeUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(storeUrl)}`, '_blank');
  };

  const shareOnTwitter = () => {
    const text = `Check out my store!`;
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(storeUrl)}&text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <Shell>
      <div style={{ maxWidth: '950px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '0.375rem'
          }}>
            View & Share Store
          </h1>
          <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Preview your store and share it with customers
          </p>
        </div>

      {/* Store Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '0.875rem',
        marginBottom: '1.25rem'
      }}>
        {[
          { label: 'Products', value: stats.totalProducts, color: '#059669' },
          { label: 'Active', value: stats.activeProducts, color: '#10b981' },
          { label: 'Orders', value: stats.totalOrders, color: '#f59e0b' },
          { label: 'Views', value: stats.storeViews, color: '#8b5cf6' }
        ].map((stat, index) => (
          <div
            key={index}
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              padding: '1.25rem 0.875rem',
              textAlign: 'center'
            }}
          >
            <div style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: stat.color,
              marginBottom: '0.375rem'
            }}>
              {stat.value}
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: '#6b7280',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Main CTA and QR Code */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
        {/* Main CTA */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          padding: '2rem 1.5rem',
          textAlign: 'center'
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 1rem', display: 'block' }}>
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>

          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '0.5rem'
          }}>
            {store.name}
          </h2>

          <div style={{
            fontSize: '0.875rem',
            color: '#6b7280',
            marginBottom: '1.5rem',
            lineHeight: '1.5'
          }}>
            {(() => {
              const description = store.description || 'Your online store is ready!';
              const maxLength = 100;

              if (description.length <= maxLength) {
                return <span>{description}</span>;
              }

              return (
                <span>
                  {isDescriptionExpanded ? description : `${description.substring(0, maxLength)}...`}
                  {' '}
                  <span
                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                    style={{
                      color: '#059669',
                      cursor: 'pointer',
                      fontWeight: '600',
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    {isDescriptionExpanded ? 'Read less' : 'Read more'}
                  </span>
                </span>
              );
            })()}
          </div>

          <button
            onClick={() => window.open(storeUrl, '_blank')}
            style={{
              padding: '0.75rem 2rem',
              backgroundColor: '#059669',
              color: 'white',
              fontSize: '0.9375rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#047857';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#059669';
            }}
          >
            View Live Store
          </button>
        </div>

        {/* QR Code */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '1rem'
          }}>
            QR Code
          </h3>

          {qrCodeDataUrl ? (
            <div>
              <div style={{
                padding: '0.75rem',
                background: 'white',
                borderRadius: '8px',
                marginBottom: '1rem',
                display: 'inline-block',
                border: '1px solid #e5e7eb'
              }}>
                <img
                  src={qrCodeDataUrl}
                  alt="Store QR Code"
                  style={{
                    width: '100%',
                    maxWidth: '160px',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </div>
              <p style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                marginBottom: '1rem',
                lineHeight: '1.4'
              }}>
                Scan to visit store
              </p>
              <a
                href={qrCodeDataUrl}
                download={`${store.slug}-qr-code.png`}
                style={{
                  display: 'inline-block',
                  padding: '0.625rem 1.25rem',
                  backgroundColor: '#f0fdf4',
                  color: '#059669',
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  textDecoration: 'none',
                  border: '1px solid #d1fae5',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#d1fae5';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f0fdf4';
                }}
              >
                Download QR
              </a>
            </div>
          ) : (
            <div style={{
              padding: '2.5rem 1rem',
              color: '#9ca3af'
            }}>
              Generating QR Code...
            </div>
          )}
        </div>
      </div>

      {/* Share Links */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        padding: '1.5rem'
      }}>
        <h3 style={{
          fontSize: '1rem',
          fontWeight: '600',
          color: '#111827',
          marginBottom: '1.25rem',
          paddingBottom: '0.625rem',
          borderBottom: '1px solid #e5e7eb'
        }}>
          Share Your Store
        </h3>

          {/* Store URL */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.8125rem',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Store URL
            </label>
            <div style={{
              display: 'flex',
              gap: '0.5rem'
            }}>
              <input
                type="text"
                value={storeUrl}
                readOnly
                style={{
                  flex: 1,
                  padding: '0.625rem 0.875rem',
                  fontSize: '0.875rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  backgroundColor: '#f9fafb',
                  fontFamily: 'monospace',
                  outline: 'none',
                  color: '#374151'
                }}
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(storeUrl);
                  toast.success('URL copied to clipboard!');
                }}
                style={{
                  padding: '0.625rem 1.25rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                }}
              >
                Copy
              </button>
            </div>
          </div>

          {/* Social Share Buttons */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.8125rem',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '0.625rem'
            }}>
              Share on Social Media
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '0.625rem'
            }}>
              <button
                onClick={shareOnWhatsApp}
                style={{
                  padding: '0.5rem 0.375rem',
                  backgroundColor: '#25D366',
                  color: 'white',
                  fontSize: '0.6875rem',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.25rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#1da851';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#25D366';
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 17.258c-.223.627-1.317 1.211-2.149 1.371-.569.108-1.309.2-3.807-.815-3.197-1.298-5.268-4.533-5.43-4.741-.16-.207-1.31-1.748-1.31-3.336 0-1.588.834-2.373 1.128-2.698.293-.325.64-.406.853-.406.213 0 .426.002.612.012.197.01.459-.074.72.549.268.64.913 2.229.994 2.391.08.16.133.349.027.556-.107.207-.16.334-.32.515-.16.181-.337.404-.48.542-.16.16-.327.333-.14.653.186.32.829 1.369 1.782 2.216 1.226 1.09 2.26 1.43 2.58 1.59.32.16.507.133.693-.08.186-.213.8-.933 1.013-1.253.213-.32.427-.267.72-.16.293.107 1.866.88 2.186 1.04.32.16.533.24.613.373.08.133.08.76-.143 1.387z"/>
                </svg>
                <span>WhatsApp</span>
              </button>

              <button
                onClick={shareOnFacebook}
                style={{
                  padding: '0.5rem 0.375rem',
                  backgroundColor: '#1877F2',
                  color: 'white',
                  fontSize: '0.6875rem',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.25rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#165dc9';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#1877F2';
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span>Facebook</span>
              </button>

              <button
                onClick={shareOnTwitter}
                style={{
                  padding: '0.5rem 0.375rem',
                  backgroundColor: '#1DA1F2',
                  color: 'white',
                  fontSize: '0.6875rem',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.25rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#1a8cd8';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#1DA1F2';
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
                <span>Twitter</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default ViewStore;
